import { useEffect, useState } from "react";
import api from "../axios";
import {
  Sparkles,
  Wallet,
  MapPin,
  Image as ImageIcon,
  UploadCloud,
  Rocket,
  RefreshCw,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  ActivitySquare,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const MEDIA_BASE = (process.env.REACT_APP_API_URL || "http://localhost:5000/api").replace(
  /\/api\/?$/,
  ""
);

const STATUS_STYLES = {
  draft: "bg-gray-100 text-gray-700",
  approved: "bg-blue-100 text-blue-700",
  rejected: "bg-red-100 text-red-700",
  published: "bg-green-100 text-green-700",
};

const VERDICT_STYLES = {
  good: { color: "text-green-700 bg-green-50 border-green-200", icon: CheckCircle2 },
  average: { color: "text-amber-700 bg-amber-50 border-amber-200", icon: ActivitySquare },
  poor: { color: "text-red-700 bg-red-50 border-red-200", icon: AlertTriangle },
};

export default function AIAdsStudio() {
  const [wallet, setWallet] = useState(0);
  const [connectedInfo, setConnectedInfo] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [insightsMap, setInsightsMap] = useState({});
  const [checkingId, setCheckingId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [form, setForm] = useState({
    service_name: "",
    location: "",
    budget_per_day: 500,
    meta_ad_account_id: "",
    extra_instructions: "",
    override_phone_number_id: "",
  });
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState("");

  const [accountBalance, setAccountBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);

  const loadWallet = async () => {
    try {
      const res = await api.get("/auth/me");
      setWallet(Number(res.data.wallet || 0));
    } catch (e) {
      console.error(e);
    }
  };

  const loadConnectedInfo = async () => {
    try {
      const res = await api.get("/ai-ads/connected-info");
      setConnectedInfo(res.data?.connected ? res.data.data : null);
    } catch (e) {
      console.error(e);
    }
  };

 const loadWhatsappNumbers = async (adAccountId) => {
    try {
      const res = await api.get("/ai-ads/whatsapp-numbers", {
        params: adAccountId ? { ad_account_id: adAccountId } : {},
      });
      setWhatsappNumbers(res.data?.numbers || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadAccounts = async () => {
    try {
      const res = await api.get("/meta-ad-accounts");
      setAccounts(res.data || []);
      if (res.data?.length && !form.meta_ad_account_id) {
        setForm((f) => ({ ...f, meta_ad_account_id: res.data[0].id }));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const res = await api.get("/ai-ads");
      setDrafts(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDrafts(false);
    }
  };

  useEffect(() => {
    loadWallet();
    loadAccounts();
    loadDrafts();
    loadConnectedInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadWhatsappNumbers(form.meta_ad_account_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.meta_ad_account_id]);

  const loadAccountBalance = async (accountId) => {
    if (!accountId) {
      setAccountBalance(null);
      return;
    }
    setBalanceLoading(true);
    try {
      const res = await api.get(`/meta-ad-accounts/${accountId}/details`);
      setAccountBalance(res.data?.live || null);
    } catch (e) {
      setAccountBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  };

  useEffect(() => {
    loadAccountBalance(form.meta_ad_account_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.meta_ad_account_id]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setFilePreview(f ? URL.createObjectURL(f) : null);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.service_name.trim()) {
      setError("Product/Service ka naam likhna zaroori hai.");
      return;
    }
    if (!form.location.trim()) {
      setError("Location likhna zaroori hai (jaise: Delhi, Noida 15km).");
      return;
    }

    setGenerating(true);
    setLastResult(null);

    try {
      const fd = new FormData();
      fd.append("service_name", form.service_name);
      fd.append("location", form.location);
      fd.append("targeting_instruction", form.location);
      fd.append("budget_per_day", form.budget_per_day);
      fd.append("extra_instructions", form.extra_instructions);
      if (form.meta_ad_account_id) fd.append("meta_ad_account_id", form.meta_ad_account_id);
      if (form.override_phone_number_id) {
        fd.append("override_phone_number_id", form.override_phone_number_id);
        const selectedNum = whatsappNumbers.find(
          (n) => n.phone_number_id === form.override_phone_number_id
        );
        if (selectedNum) fd.append("override_display_phone", selectedNum.display_phone_number || "");
      }
      if (file) fd.append("media", file);

      const res = await api.post("/ai-ads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setLastResult(res.data);
      setFile(null);
      setFilePreview(null);
      setForm((f) => ({ ...f, service_name: "", extra_instructions: "" }));
      loadDrafts();
    } catch (err) {
      setError(err.response?.data?.message || "Ad generate karne me error aayi.");
    } finally {
      setGenerating(false);
    }
  };

  const updateStatus = async (draft, status) => {
    setBusyId(draft.id);
    try {
      await api.put(`/ai-ads/${draft.id}`, {
        ai_primary_text: draft.ai_primary_text,
        ai_headline: draft.ai_headline,
        ai_description: draft.ai_description,
        ai_cta: draft.ai_cta,
        ai_audience: draft.ai_audience,
        ai_budget_note: draft.ai_budget_note,
        status,
      });
      loadDrafts();
    } catch (e) {
      alert("Status update failed");
    } finally {
      setBusyId(null);
    }
  };

  const publishDraft = async (draft) => {
    if (!draft.meta_ad_account_id) {
      alert("Pehle is draft ke liye Meta Ad Account select karein.");
      return;
    }
    setBusyId(draft.id);
    try {
      const res = await api.post(`/ai-ads/${draft.id}/publish`);
      alert(res.data.message || "Ad published (PAUSED) on Meta ✅");
      loadDrafts();
      loadWallet();
    } catch (e) {
      alert(e.response?.data?.message || "Publish failed");
    } finally {
      setBusyId(null);
    }
  };

  const deleteDraft = async (id) => {
    if (!window.confirm("Ye ad draft delete karna hai?")) return;
    setBusyId(id);
    try {
      await api.delete(`/ai-ads/${id}`);
      loadDrafts();
    } catch (e) {
      alert("Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const toggleLive = async (draft, action) => {
    setBusyId(draft.id);
    try {
      const res = await api.post(`/ai-ads/${draft.id}/activate`, { action });
      alert(res.data.message);
      setDrafts((prev) =>
        prev.map((d) =>
          d.id === draft.id ? { ...d, meta_live_status: res.data.meta_status } : d
        )
      );
    } catch (e) {
      alert(e.response?.data?.message || "Status update failed");
    } finally {
      setBusyId(null);
    }
  };

  const checkHealth = async (id) => {
    setCheckingId(id);
    try {
      const res = await api.get(`/ai-ads/${id}/insights`);
      setInsightsMap((m) => ({ ...m, [id]: res.data }));
      setExpandedId(id);
      loadDrafts();
    } catch (e) {
      alert(e.response?.data?.message || "Insight check failed. Ad shayad abhi tak Meta approve/deliver nahi hua.");
    } finally {
      setCheckingId(null);
    }
  };

  const mediaSrc = (url) => (url ? `${MEDIA_BASE}${url}` : null);

  const selectedAccountForBalance = accounts.find(
    (a) => String(a.id) === String(form.meta_ad_account_id)
  );

  return (
    <div className="w-full min-h-full bg-gray-50 px-4 py-6 md:px-8 md:py-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="text-purple-600" size={24} />
              AI Ads Studio
            </h1>
            <p className="text-sm text-gray-500 mt-1 max-w-xl">
              Sirf product/service aur location batayein — AI baaki sab bana dega (creative, copy, targeting) aur seedha Meta Ads par publish karega.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 bg-white border rounded-xl px-4 py-3 shadow-sm">
              <Wallet size={18} className="text-green-600" />
              <div>
                <div className="text-[11px] text-gray-500 leading-none">CRM Wallet</div>
                <div className="font-bold text-gray-900">₹{wallet}</div>
              </div>
            </div>

            {form.meta_ad_account_id && (
              <div className="flex items-center gap-3 bg-white border rounded-xl px-4 py-3 shadow-sm">
                <Wallet size={18} className="text-blue-600" />
                <div>
                  <div className="text-[11px] text-gray-500 leading-none">Meta Ad Account</div>
                  <div className="font-bold text-gray-900 text-sm">
                    {balanceLoading ? (
                      "Loading..."
                    ) : accountBalance ? (
                      accountBalance.remaining_estimate !== undefined ? (
                        <>
                          {accountBalance.currency} {(Number(accountBalance.remaining_estimate) / 100).toFixed(2)}
                          <span className="text-gray-400 font-normal"> remaining</span>
                        </>
                      ) : (
                        <>
                          {accountBalance.currency}{" "}
                          {accountBalance.balance ? (Number(accountBalance.balance) / 100).toFixed(2) : "0.00"}
                          <span className="text-gray-400 font-normal"> outstanding (owed)</span>
                        </>
                      )
                    ) : (
                      "N/A"
                    )}
                  </div>
                </div>
                <a
                  href={`https://www.facebook.com/ads/manager/account_settings/account_billing/?act=${String(
                    selectedAccountForBalance?.ad_account_id || ""
                  ).replace("act_", "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-semibold bg-blue-600 text-white px-3 py-1.5 rounded-lg whitespace-nowrap ml-1"
                >
                  Add Funds ↗
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Create form */}
        <form onSubmit={handleGenerate} className="bg-white rounded-2xl shadow-sm border p-6 md:p-8 mb-8">
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Product / Service <span className="text-red-500">*</span>
              </label>
              <input
                value={form.service_name}
                onChange={(e) => setForm({ ...form, service_name: e.target.value })}
                placeholder="e.g. Hair Transplant, IVF Consultation, Bakery Cakes"
                className="border border-gray-300 rounded-lg px-3 py-2.5 w-full mt-1.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                <MapPin size={14} /> Target Location <span className="text-red-500">*</span>
              </label>
              <input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="e.g. Noida 15km, Delhi, Uttar Pradesh (pura state)"
                className="border border-gray-300 rounded-lg px-3 py-2.5 w-full mt-1.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">Daily Budget (₹)</label>
              <input
                type="number"
                min={100}
                value={form.budget_per_day}
                onChange={(e) => setForm({ ...form, budget_per_day: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2.5 w-full mt-1.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
              />
            </div>
            
            <div>
              <label className="text-sm font-semibold text-gray-700">WhatsApp Number</label>
              <select
                value={form.override_phone_number_id}
                onChange={(e) => setForm({ ...form, override_phone_number_id: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2.5 w-full mt-1.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
              >
                <option value="">Default (Ad Account wala number use hoga)</option>
                {whatsappNumbers.map((n) => (
                  <option key={n.phone_number_id} value={n.phone_number_id}>
                    {n.display_phone_number || n.phone_number_id} {n.verified_name ? `(${n.verified_name})` : ""}
                  </option>
                ))}
              </select>
              {!whatsappNumbers.length && (
                <p className="text-xs text-gray-400 mt-1.5">
                  Koi extra WhatsApp number nahi mila — default account wala number use hoga.
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">Meta Ad Account</label>
              <select
                value={form.meta_ad_account_id}
                onChange={(e) => setForm({ ...form, meta_ad_account_id: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2.5 w-full mt-1.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
              >
                <option value="">Select ad account (baad me bhi select kar sakte hain)</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.ad_account_name} ({a.ad_account_id})
                  </option>
                ))}
              </select>
              {!accounts.length && (
                <p className="text-xs text-amber-600 mt-1.5">
                  Koi Meta Ad Account connect nahi hai. Settings me jaake pehle apna Ad Account connect karein.
                </p>
              )}

              {(() => {
                const selectedAcc = accounts.find(
                  (a) => String(a.id) === String(form.meta_ad_account_id)
                );
                if (!selectedAcc) return null;

                const accPhone = selectedAcc.phone_number_id;
                const connectedPhone = connectedInfo?.phone_number_id;
                const displayPhone =
                  selectedAcc.display_phone_number || connectedInfo?.display_phone_number;

                if (!accPhone) {
                  return (
                    <p className="text-xs text-amber-600 mt-1.5">
                      Is ad account me koi WhatsApp number linked nahi hai. Admin panel me is account ke liye Phone Number ID set karayein.
                    </p>
                  );
                }

                if (connectedPhone && accPhone !== connectedPhone) {
                  return (
                    <p className="text-xs text-red-600 mt-1.5">
                      ⚠️ Ye account ek alag WhatsApp number ({displayPhone || accPhone}) se linked hai — jo aapke AI Chatbot number se match nahi karta.
                    </p>
                  );
                }

                return (
                  <p className="text-xs text-green-600 mt-1.5">
                    ✅ Ye ad isi WhatsApp number par chalegi: {displayPhone || accPhone}
                  </p>
                );
              })()}
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-gray-700">
                Offer / Extra Instructions (optional)
              </label>
              <textarea
                rows={2}
                value={form.extra_instructions}
                onChange={(e) => setForm({ ...form, extra_instructions: e.target.value })}
                placeholder="e.g. 20% discount is week, ya koi special offer batana hai"
                className="border border-gray-300 rounded-lg px-3 py-2.5 w-full mt-1.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                <ImageIcon size={14} /> Image / Video (optional)
              </label>
              <div className="flex flex-wrap items-center gap-4 mt-1.5">
                <label className="flex items-center gap-2 border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 cursor-pointer text-sm text-gray-600 hover:border-purple-400 hover:text-purple-600 transition">
                  <UploadCloud size={18} />
                  {file ? file.name : "Apna image/video upload karein"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,video/mp4,video/webm"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
                {filePreview && file?.type.startsWith("image") && (
                  <img src={filePreview} alt="preview" className="w-16 h-16 rounded-lg object-cover border" />
                )}
                {filePreview && file?.type.startsWith("video") && (
                  <video
                    src={filePreview}
                    className="w-16 h-16 rounded-lg object-cover border bg-black"
                    muted
                    controls
                  />
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                Upload nahi karenge to Gemini AI khud se ek professional ad image bana dega.
              </p>
            </div>
          </div>

          {error && (
            <div className="mt-5 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={generating}
            className="mt-6 inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold px-6 py-2.5 rounded-lg shadow hover:opacity-90 disabled:opacity-50"
          >
            {generating ? (
              <>
                <RefreshCw size={16} className="animate-spin" /> AI Ad Generate ho raha hai...
              </>
            ) : (
              <>
                <Sparkles size={16} /> Generate AI Ad
              </>
            )}
          </button>
        </form>

        {/* Just-generated preview */}
        {lastResult && (
          <div className="bg-white border border-purple-200 rounded-2xl shadow-sm p-6 md:p-8 mb-8">
            <div className="flex items-center gap-2 text-purple-700 font-semibold mb-4">
              <CheckCircle2 size={18} /> Naya AI Ad Draft Ready Hai
              {lastResult.media_source === "ai" && (
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                  AI-generated image
                </span>
              )}
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {lastResult.media_url && String(lastResult.media_type || "").startsWith("video") ? (
                <video
                  src={mediaSrc(lastResult.media_url)}
                  controls
                  className="rounded-xl w-full h-52 object-cover border bg-black"
                />
              ) : lastResult.media_url ? (
                <img
                  src={mediaSrc(lastResult.media_url)}
                  alt="ad creative"
                  className="rounded-xl w-full h-52 object-cover border"
                />
              ) : null}
              <div className="md:col-span-2 space-y-2 text-sm">
                <p><span className="font-semibold">Headline:</span> {lastResult.ai_headline}</p>
                <p><span className="font-semibold">Primary Text:</span> {lastResult.ai_primary_text}</p>
                <p><span className="font-semibold">Description:</span> {lastResult.ai_description}</p>
                <p><span className="font-semibold">CTA:</span> {lastResult.ai_cta}</p>
                <p><span className="font-semibold">Audience:</span> {lastResult.ai_audience}</p>
                <p className="text-gray-500">{lastResult.ai_budget_note}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Neeche list me is draft ko "Approve" karke Publish karein.
            </p>
          </div>
        )}

        {/* All ads list */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Sabhi AI Ads (Naye + Purane)</h2>
          <button onClick={loadDrafts} className="text-sm text-gray-500 flex items-center gap-1 hover:text-gray-800">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {loadingDrafts ? (
          <p className="text-gray-500 text-sm">Loading...</p>
        ) : !drafts.length ? (
          <p className="text-gray-500 text-sm">Abhi tak koi AI ad nahi banaya gaya.</p>
        ) : (
          <div className="space-y-4 pb-8">
            {drafts.map((d) => {
              const isOpen = expandedId === d.id;
              const insight = insightsMap[d.id];
              const verdict = insight?.analysis?.verdict || d.last_health_status;
              const vStyle = VERDICT_STYLES[verdict] || null;

              return (
                <div key={d.id} className="bg-white border rounded-2xl shadow-sm overflow-hidden">
                  <div className="p-4 md:p-5 flex flex-wrap gap-4 items-center">
                    {d.media_url && String(d.media_type || "").startsWith("video") ? (
                      <video
                        src={mediaSrc(d.media_url)}
                        className="w-16 h-16 rounded-lg object-cover border flex-shrink-0 bg-black"
                        muted
                      />
                    ) : d.media_url ? (
                      <img
                        src={mediaSrc(d.media_url)}
                        alt=""
                        className="w-16 h-16 rounded-lg object-cover border flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <ImageIcon size={20} className="text-gray-400" />
                      </div>
                    )}

                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{d.service_name}</span>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[d.status] || "bg-gray-100 text-gray-700"}`}>
                        {d.status}
                      </span>
                      {d.status === "published" && d.meta_live_status && (
                        <span
                          className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                            d.meta_live_status === "ACTIVE"
                              ? "bg-green-100 text-green-700"
                              : d.meta_live_status === "PAUSED"
                              ? "bg-gray-200 text-gray-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {d.meta_live_status === "ACTIVE"
                            ? "🟢 Live"
                            : d.meta_live_status === "PAUSED"
                            ? "⏸️ Paused"
                            : `🔴 ${d.meta_live_status}`}
                        </span>
                      )}
                        {d.media_source === "ai" && (
                          <span className="text-[11px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                            AI creative
                          </span>
                        )}
                        {vStyle && (
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${vStyle.color}`}>
                            {verdict === "good" ? "Achha chal raha hai" : verdict === "poor" ? "Kamzor performance" : "Average"}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">
                        <MapPin size={12} /> {d.location || d.targeting_instruction} &nbsp;•&nbsp; ₹{d.budget_per_day}/day
                        {d.ad_account_name ? ` • ${d.ad_account_name}` : ""}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      {d.status === "draft" && (
                        <button
                          onClick={() => updateStatus(d, "approved")}
                          disabled={busyId === d.id}
                          className="text-xs font-semibold bg-blue-600 text-white px-3 py-1.5 rounded-lg disabled:opacity-50"
                        >
                          Approve
                        </button>
                      )}
                      {d.status === "approved" && (
                        <button
                          onClick={() => publishDraft(d)}
                          disabled={busyId === d.id}
                          className="text-xs font-semibold bg-green-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
                        >
                          <Rocket size={13} /> Publish to Meta
                        </button>
                      )}
                      {d.status === "published" && (
                        <>
                          <button
                            onClick={() => toggleLive(d, d.meta_live_status === "ACTIVE" ? "pause" : "activate")}
                            disabled={busyId === d.id}
                            className={`text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50 text-white ${
                              d.meta_live_status === "ACTIVE" ? "bg-amber-600" : "bg-green-600"
                            }`}
                          >
                            <Rocket size={13} />
                            {d.meta_live_status === "ACTIVE" ? "Pause Ad" : "Go Live"}
                          </button>
                          <button
                            onClick={() => checkHealth(d.id)}
                            disabled={checkingId === d.id}
                            className="text-xs font-semibold bg-indigo-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
                          >
                            <ActivitySquare size={13} />
                            {checkingId === d.id ? "Checking..." : "AI Health Check"}
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => setExpandedId(isOpen ? null : d.id)}
                        className="text-xs text-gray-600 border px-2.5 py-1.5 rounded-lg flex items-center gap-1"
                      >
                        Details {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                      </button>
                      <button
                        onClick={() => deleteDraft(d.id)}
                        disabled={busyId === d.id}
                        className="text-xs text-red-600 border border-red-200 px-2 py-1.5 rounded-lg"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="border-t bg-gray-50 p-4 md:p-5 grid md:grid-cols-2 gap-6 text-sm">
                      <div className="space-y-1.5">
                        <p><span className="font-semibold">Headline:</span> {d.ai_headline}</p>
                        <p><span className="font-semibold">Primary Text:</span> {d.ai_primary_text}</p>
                        <p><span className="font-semibold">Description:</span> {d.ai_description}</p>
                        <p><span className="font-semibold">CTA:</span> {d.ai_cta}</p>
                        <p><span className="font-semibold">Audience:</span> {d.ai_audience}</p>
                        <p><span className="font-semibold">Keywords:</span> {d.ai_keywords || "-"}</p>
                        <p><span className="font-semibold">Marketing Angle:</span> {d.ai_marketing_angle || "-"}</p>
                      </div>

                      <div className="space-y-1.5">
                        <p><span className="font-semibold">Campaign ID:</span> {d.meta_campaign_id || "-"}</p>
                        <p><span className="font-semibold">Ad Set ID:</span> {d.meta_adset_id || "-"}</p>
                        <p><span className="font-semibold">Creative ID:</span> {d.meta_creative_id || "-"}</p>
                        <p><span className="font-semibold">Ad ID:</span> {d.meta_ad_id || "-"}</p>
                        <p><span className="font-semibold">Wallet Charged:</span> {d.wallet_charge ? `₹${d.wallet_charge}` : "-"}</p>
                        <p><span className="font-semibold">Created:</span> {new Date(d.created_at).toLocaleString()}</p>

                        {insight && (
                          <div className={`mt-3 border rounded-lg p-3 ${vStyle?.color || "bg-white"}`}>
                            <p className="font-semibold flex items-center gap-1">
                              {vStyle?.icon ? <vStyle.icon size={14} /> : null}
                              AI Verdict: {insight.analysis?.verdict?.toUpperCase()}
                            </p>
                            <p className="mt-1">{insight.analysis?.summary}</p>
                            <p className="mt-1 text-gray-600"><span className="font-semibold">Suggestions:</span> {insight.analysis?.suggestions}</p>
                            <div className="grid grid-cols-2 gap-1 mt-2 text-xs">
                              <span>Impressions: {insight.insight.impressions}</span>
                              <span>Reach: {insight.insight.reach}</span>
                              <span>Clicks: {insight.insight.clicks}</span>
                              <span>CTR: {insight.insight.ctr}%</span>
                              <span>Spend: ₹{insight.insight.spend}</span>
                              <span>Messages: {insight.insight.messages}</span>
                            </div>
                          </div>
                        )}
                        {!insight && d.last_health_status && (
                          <div className="mt-3 text-xs text-gray-500">
                            Last check: {d.last_checked_at ? new Date(d.last_checked_at).toLocaleString() : "-"} • Status: {d.last_health_status}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}